import { type ReactNode, createContext, useContext } from 'react';

import type { NoteGenerationErrorProps } from '@repo/videa-notes';

import type { VideaNotesUIState, VoiceNotesSource } from '../types';

/**
 * Patient data type used across VideaNotes contexts.
 */
export type VideaNotesPatient = {
  pmsPatientId?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  sourcePatientId?: string;
  imagingPatientId?: string;
  videaId?: string;
  dob?: string;
};

/**
 * Visit data type for patient creation.
 */
export type PatientVisitData = {
  id: string;
  firstName: string;
  lastName: string | null;
  sourcePatientId: string | null;
  dateOfBirth: Date | null;
  appointmentDate: Date;
  appointmentStartTime: string;
};

/**
 * Core context value interface.
 * Contains shared foundation state used by all views.
 */
export interface VideaNotesCoreContextValue {
  // UI State
  uiStatus: VideaNotesUIState;
  voiceNotesSource: VoiceNotesSource;

  // Patient data
  patientName: string;
  patient: VideaNotesPatient | undefined;
  standalonePatientVisitId: string | undefined;

  // Preferences
  isVoiceNotesConsentEnabled: boolean;
  isVoiceNotesCustomTemplatesEnabled: boolean;

  // Error state
  processingError: NoteGenerationErrorProps | undefined;

  // Shared handlers
  onCloseSheet: () => void;
  onPatientCreated: (visitData: PatientVisitData) => void;
}

const VideaNotesCoreContext = createContext<VideaNotesCoreContextValue | null>(
  null,
);

interface VideaNotesCoreProviderProps {
  value: VideaNotesCoreContextValue;
  children: ReactNode;
}

/**
 * Provider component for VideaNotes core context.
 * Contains shared foundation state used by all views.
 */
export const VideaNotesCoreProvider = ({
  value,
  children,
}: VideaNotesCoreProviderProps) => {
  return (
    <VideaNotesCoreContext.Provider value={value}>
      {children}
    </VideaNotesCoreContext.Provider>
  );
};

/**
 * Hook to access VideaNotes core context.
 * Must be used within a VideaNotesCoreProvider.
 */
export const useVideaNotesCoreContext = (): VideaNotesCoreContextValue => {
  const context = useContext(VideaNotesCoreContext);
  if (!context) {
    throw new Error(
      'useVideaNotesCoreContext must be used within a VideaNotesCoreProvider',
    );
  }
  return context;
};
